"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const fmko_ts_common_1 = require("fmko-ts-common");
const fmko_ts_widgets_1 = require("fmko-ts-widgets");
const GridSelectionState_1 = tslib_1.__importDefault(require("../GridSelectionState"));
const GridConstants_1 = tslib_1.__importDefault(require("../GridConstants"));
class DefaultDragHandler {
    createDragProxy(data, rows) {
        return new fmko_ts_widgets_1.Label(`Drag Proxy; rows=[${rows.join(", ")}]`);
    }
}
class GridDragAndDropController {
    constructor(sourceGrid) {
        this.sourceGrid = sourceGrid;
        sourceGrid.addMouseDownHandler((event) => {
            if (this.maybeDragStart(event)) {
                event.preventDefault();
            }
        });
        this.selectionState = new GridSelectionState_1.default(sourceGrid);
        this.dragHandler = new DefaultDragHandler();
    }
    getDragHandler() {
        return this.dragHandler;
    }
    getDropHandler() {
        return this.dropHandler;
    }
    setDragHandler(dragHandler) {
        this.dragHandler = dragHandler;
    }
    setDropHandler(dropHandler) {
        this.dropHandler = dropHandler;
    }
    onMouseMove(event) {
        if (!this.sourceData) {
            if (this.dist(this.dragStartX, this.dragStartY, event.clientX, event.clientY) > GridDragAndDropController.DRAG_THRESHOLD) {
                this.dragStart(event);
            }
        }
        this.dragMove(event);
        event.preventDefault();
    }
    onMouseUp(event) {
        this.dragEnd(event);
    }
    addDropTarget(dropTarget) {
        // Only support a single target for now..
        this.targetGrid = dropTarget;
    }
    maybeDragStart(event) {
        const ctrlKey = event.ctrlKey;
        const shiftKey = event.shiftKey;
        if (ctrlKey || shiftKey) {
            return false;
        }
        this.draggedRow = this.getRowFromEvent(this.sourceGrid, event);
        if (this.draggedRow) {
            this.dragStartX = event.clientX;
            this.dragStartY = event.clientY;
            document.addEventListener("mousemove", this.mouseMoveListener = this.onMouseMove.bind(this));
            document.addEventListener("mouseup", this.mouseUpListener = this.onMouseUp.bind(this));
            return true;
        }
        return false;
    }
    dragStart(event) {
        this.sourceData = [];
        if (this.draggedRow) {
            if (this.selectionState) {
                if (!this.selectionState.isSelected(this.draggedRow)) {
                    this.selectionState.clearSelected();
                    this.selectionState.select(this.draggedRow);
                }
                const selected = this.selectionState.getSelected();
                for (let row of selected) {
                    const rowData = this.sourceGrid.getDataForTableRow(row);
                    if (rowData) {
                        rowData.forEach((data) => {
                            this.sourceData.push(data);
                        });
                    }
                }
                this.dragProxy = this.dragHandler.createDragProxy([...this.sourceData], selected);
            }
            else {
                const rowData = this.sourceGrid.getDataForTableRow(this.draggedRow);
                if (rowData) {
                    rowData.forEach((data) => {
                        this.sourceData.push(data);
                    });
                }
                this.dragProxy = this.dragHandler.createDragProxy([...this.sourceData], [this.draggedRow]);
            }
        }
        if (this.dragProxy) {
            const element = this.dragProxy.element;
            document.body.appendChild(element);
            // Position
            element.style.position = "fixed";
            this.setPositionFromEvent(event, this.dragProxy);
        }
    }
    dragMove(event) {
        if (this.dragProxy) {
            this.setPositionFromEvent(event, this.dragProxy);
            const oldDropTR = this.dropTR;
            this.dropTR = null;
            const cell = this.targetGrid ? this.targetGrid.getElementForEvent(event) : null;
            if (cell) {
                const tr = cell.parentElement;
                const rowIndex = fmko_ts_common_1.DOM.indexOf(tr.parentElement, tr) - 1;
                if (rowIndex >= 0) {
                    if (tr) {
                        if (tr !== oldDropTR) {
                            tr?.classList.add(GridConstants_1.default.GRID_DROP_TARGET);
                            this.dropTR = tr;
                        }
                        else {
                            this.dropTR = oldDropTR;
                        }
                    }
                }
            }
            if (oldDropTR && oldDropTR !== this.dropTR) {
                oldDropTR?.classList.remove(GridConstants_1.default.GRID_DROP_TARGET);
            }
            // event koordinater oversat til parent frame's koordinatsystem: Check om clientY er udenfor øvre/nedre kant
            const clientY = event.clientY;
            const scrollTop = this.getScrollTop();
            const clientHeight = document.body.clientHeight;
            if (clientY < 0 && scrollTop > 0) {
                this.scrollY(-this.throttle(-clientY, 5, 0.25));
            }
            else if (clientY > clientHeight && this.isAtScrollTop(scrollTop, clientHeight)) {
                this.scrollY(this.throttle(clientY - clientHeight, 5, 0.25));
            }
            else {
                this.scrollY(0);
            }
        }
    }
    dragEnd(event) {
        // Detach listeners from document
        document.removeEventListener("mousemove", this.mouseMoveListener);
        document.addEventListener("mouseup", this.mouseUpListener);
        this.scrollY(0);
        let dragEndsNow = true;
        if (this.sourceData && this.dragProxy) {
            document.body.removeChild(this.dragProxy.element);
            const row = this.targetGrid ? this.getRowFromEvent(this.targetGrid, event) : null;
            if (row) {
                const targetData = this.targetGrid.getDataForTableRow(row);
                // Only support a single target (targetData.length == 1)
                if (targetData && targetData.length >= 1 && this.dropHandler) {
                    dragEndsNow = this.dropHandler.onDrop([...this.sourceData], targetData, {
                        dragEnded: () => {
                            if (this.dropTR) {
                                this.dropTR?.classList.remove(GridConstants_1.default.GRID_DROP_TARGET);
                            }
                        }
                    });
                }
            }
        }
        if (dragEndsNow && this.dropTR) {
            this.dropTR?.classList.remove(GridConstants_1.default.GRID_DROP_TARGET);
        }
        this.dragProxy = null;
        this.sourceData = null;
    }
    getPageYOffsetForParentWindow() {
        return window.pageYOffset;
    }
    getScrollTop() {
        return this.getPageYOffsetForParentWindow();
    }
    getRowFromEvent(grid, event) {
        const cell = grid.getElementForEvent(event);
        if (!cell) {
            return null;
        }
        const tr = cell.parentElement;
        if (tr?.classList.contains(GridConstants_1.default.GRID_ROW_EXPAND_EXPANDED)) {
            return null;
        }
        const topElementFromEvent = tr.parentElement.parentElement.parentElement;
        const topElementFromGrid = grid.element;
        if (topElementFromEvent !== topElementFromGrid) {
            return null;
        }
        return tr;
    }
    setPositionFromEvent(event, dragProxy) {
        const x = event.clientX;
        const y = event.clientY;
        const proxyStyle = dragProxy.element.style;
        // Give proxy widget an x offset relative to the mouse cursor. This
        // reduces the risk that the proxy widget is treated as the drop target
        // (seen with FireFox and Safari).
        proxyStyle.left = `${x + 5}px`;
        proxyStyle.top = `${y - 9}px`;
        // center, not the top aligned
        // with the mouse. But can we
        // determine the height of the
        // proxy widget in a general
        // way?
    }
    throttle(input, maxOutput, factor) {
        let result = ~~(input * factor); // truncate to "int"
        if (result > maxOutput) {
            result = maxOutput;
        }
        return result;
    }
    scrollY(step) {
        if (this.scrollStep === step) {
            // Already doing what we are asked
        }
        else if (step === 0) {
            // Stop scrolling
            if (this.scrollTimer) {
                this.scrollTimer.cancel();
            }
        }
        else {
            // Start scrolling
            if (!this.scrollTimer) {
                this.scrollTimer = new fmko_ts_common_1.FunctionalTimer(() => {
                    scrollBy(0, this.scrollStep);
                });
            }
            this.scrollTimer.scheduleRepeating(10);
        }
        this.scrollStep = step;
    }
    dist(dragStartX2, dragStartY2, clientX, clientY) {
        const deltaX = clientX - this.dragStartX;
        const deltaY = clientY - this.dragStartY;
        return ~~Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    }
    isAtScrollTop(scrollTop, clientHeight) {
        const scrollHeight = document.body.scrollHeight;
        return scrollTop < scrollHeight - clientHeight;
    }
}
GridDragAndDropController.DRAG_THRESHOLD = 3;
exports.default = GridDragAndDropController;
//# sourceMappingURL=GridDragAndDropController.js.map